import React, { memo, ReactElement, useEffect, useRef } from 'react';
import { Options } from 'highcharts';
import { Chart } from '@dexma/ui-components';

import Branding from 'domain/entity/branding';
import { LayoutType } from 'domain/entity/layout';
import PieData from 'domain/entity/widgets/PieData';
import { getColor, getPeriodFrom, getPeriodTo } from 'adapter/in/common/utils/widgetDataUtils';
import DataAvailability from 'adapter/in/common/components/widgets/DataAvailability';
import { PeriodType } from 'domain/entity/widgetData';
import { checkMemoChanges } from 'adapter/in/common/components/widgets/common/checkRedraw';
import { ChartRefObject, useChartExport } from 'adapter/in/advanced-analytics/context/ChartExport';

type PieProps = {
    height: number;
    width: number;
    isOnEdit: boolean;
    data: PieData;
    decimalSeparator: string;
    thousandSeparator: string;
    branding: Branding;
    isLoading: boolean;
    showLegend: boolean;
    showDataAvailability: boolean;
    dateFormat: string;
    layoutType?: LayoutType;
    widgetWidth?: number;
    widgetHeight?: number;
    isReportTemplate?: boolean;
};

const fakeDataPie = [
    { name: 'something', y: 23 },
    { name: 'another', y: 26 },
];

const Pie = (props: PieProps): ReactElement => {
    const {
        height,
        width,
        isOnEdit,
        data,
        decimalSeparator,
        thousandSeparator,
        branding,
        isLoading,
        showLegend,
        showDataAvailability,
        dateFormat,
        layoutType,
        widgetWidth,
        widgetHeight,
        isReportTemplate,
    } = props;
    const chartComponentRef = useRef<ChartRefObject>(null);
    const { setChartRef } = useChartExport();

    useEffect(() => {
        if (setChartRef && chartComponentRef !== null) {
            setChartRef(chartComponentRef);
        }
    }, [setChartRef]);

    const from = isLoading ? 'last seven days' : getPeriodFrom(data.description.period, dateFormat);
    let to = isLoading ? ' ' : getPeriodTo(data.description.period, dateFormat);

    to = to !== '' ? `- ${to}` : to;
    const subtitle = data.description.period.type === PeriodType.INTERVAL ? `${from} ${to}` : from.charAt(0).toUpperCase() + from.slice(1);
    const isCompactReportTemplatePie =
        !!isReportTemplate && layoutType === LayoutType.A4 && (widgetWidth ?? Number.POSITIVE_INFINITY) <= 5 && (widgetHeight ?? Number.POSITIVE_INFINITY) <= 7;

    const chartContract = {
        chart: {
            zooming: {
                type: isOnEdit || isLoading ? undefined : 'x',
            },
            height: height - 4,
            width: width - 4,
            type: 'pie',
            borderRadius: 8,
            ...(isCompactReportTemplatePie
                ? {
                      spacing: [0, 0, 0, 0],
                      spacingTop: 0,
                      spacingBottom: 0,
                      marginBottom: 0,
                  }
                : {}),
        },
        title: {
            text: isLoading ? 'Sample title' : data.description.title,
            ...(isCompactReportTemplatePie ? { floating: true, margin: 0, y: 14 } : {}),
        },
        ...(isCompactReportTemplatePie
            ? {}
            : {
                  subtitle: {
                      text: isLoading ? 'Sample subtitle' : subtitle,
                  },
              }),
        tooltip: {
            enabled: !isLoading && !isOnEdit,
            shared: !isOnEdit,
            valueSuffix: ` ${data.units}`,
            valueDecimals: 2,
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {point.name}: <b> {point.y} </b><br/>',
            headerFormat: isLoading ? undefined : `<span style="font-size:10px"> ${from} ${to}<span><br/>`,
        },
        plotOptions: {
            pie: {
                allowPointSelect: !isOnEdit,
                cursor: 'pointer',
                innerSize: isCompactReportTemplatePie ? '0%' : '40%',
                ...(isCompactReportTemplatePie
                    ? {
                          center: ['50%', '56%'],
                          size: '46%',
                      }
                    : {}),
                dataLabels: {
                    enabled: !isOnEdit,
                    format: isCompactReportTemplatePie ? `<strong>{point.name}</strong><br/>{point.percentage:.0f}%` : `<strong>{point.name} {point.percentage:.1f}%</strong>`,
                    ...(isCompactReportTemplatePie
                        ? {
                              distance: 12,
                              connectorPadding: 2,
                              connectorWidth: 1,
                              softConnector: true,
                              padding: 0,
                              style: {
                                  fontSize: '10px',
                                  fontWeight: 'normal',
                                  textOutline: 'none',
                              },
                              crop: false,
                              overflow: 'justify',
                          }
                        : {}),
                },
                showInLegend: !isOnEdit && !isCompactReportTemplatePie,
            },
        },
        series: [
            {
                type: 'pie',
                name: isLoading ? 'Sample title' : data.title,
                colorByPoint: true, // REVIEW Not part of Pie options type
                data: isLoading
                    ? fakeDataPie
                    : data.series.map((series) => ({
                          name: series.title,
                          y: series.value,
                          color:
                              series.fillPattern === 'DASHED'
                                  ? {
                                        pattern: {
                                            path: {
                                                d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
                                                strokeWidth: 3,
                                                fill: getColor(branding)(series.color),
                                            },
                                            width: 10,
                                            height: 10,
                                            color: getColor(branding)(series.color),
                                            opacity: 1,
                                        },
                                    }
                                  : getColor(branding)(series.color),
                      })),
            },
        ],
        legend: {
            enabled: showLegend && !isCompactReportTemplatePie,
        },
    };

    return (
        <>
            {!isLoading && !isOnEdit && showDataAvailability && <DataAvailability dataAvailability={data.dataAvailability} />}
            <Chart
                ref={chartComponentRef}
                data-testid='pie-chart'
                thousandsSep={thousandSeparator}
                decimalPoint={decimalSeparator}
                options={chartContract as Options}
            />
        </>
    );
};

export default memo(Pie, checkMemoChanges);
